import { useContext } from 'react';

import { MAVLINK_MESSAGE_IDS as MESSAGE } from '@/define/messageId';
import { MessageContext } from '@/helpers/MessageProvider';
import useMavLinkAddress from '@/hooks/useMavLinkAddress';
import EventEmitter from '@/libs/EventEmitter';
import telemetryStore from '@/stores/telemetryStore';

const useActionCommand = () => {
  const { publishCommand } = useContext(MessageContext);
  const { getAddress } = useMavLinkAddress();

  const arm = () => (robot) => {
    const address = getAddress(robot.id);
    const execute = () => publishCommand(robot, 'action/arm', [[...address]]);

    return {
      execute,
      topic: `${robot.id}/telemetry/${MESSAGE.COMMAND_ACK}`,
      isMatch: (data) => data.command === 400,
      isSuccess: (data) => data.result === 0,
    };
  };

  const brakePX4Rover = () => (robot) => {
    const address = getAddress(robot.id);
    const { lat, lng } = telemetryStore.get(robot.id, MESSAGE.GLOBAL_POSITION_INT);
    const execute = () => publishCommand(robot, 'action/brake/px4/rover', [[...address, NaN, lat, lng]]);

    return {
      execute,
      topic: `${robot.id}/telemetry/${MESSAGE.COMMAND_ACK}`,
      isMatch: (data) => data.command === 192,
      isSuccess: (data) => data.result === 0,
    };
  };

  const changeSpeed = (speedType, speed) => (robot) => {
    const address = getAddress(robot.id);
    const execute = () => publishCommand(robot, 'action/change_speed', [[...address, speedType, speed]]);

    return {
      execute,
      topic: `${robot.id}/telemetry/${MESSAGE.COMMAND_ACK}`,
      isMatch: (data) => data.command === 178,
      isSuccess: (data) => data.result === 0,
    };
  };

  const disarm = () => (robot) => {
    const address = getAddress(robot.id);
    const execute = () => publishCommand(robot, 'action/disarm', [[...address]]);

    return {
      execute,
      topic: `${robot.id}/telemetry/${MESSAGE.COMMAND_ACK}`,
      isMatch: (data) => data.command === 400,
      isSuccess: (data) => data.result === 0,
    };
  };

  const elevateArduPilot = (altitude) => (robot) => {
    const address = getAddress(robot.id);
    const { ralt } = telemetryStore.get(robot.id, MESSAGE.GLOBAL_POSITION_INT);
    const execute = () => {
      publishCommand(robot, 'action/elevate/ardupilot', [[...address, ralt - altitude]]);
      EventEmitter.publish(`${robot.id}/elevate/done`);
    };

    return {
      execute,
      topic: `${robot.id}/elevate/done`,
      isMatch: () => true,
      isSuccess: () => true,
    };
  };

  const goto = (speed, yaw, lat, lng, altitude) => (robot) => {
    const address = getAddress(robot.id);
    const execute = () =>
      publishCommand(robot, 'action/goto', [[...address, speed, yaw, lat * 1e7, lng * 1e7, altitude]]);

    return {
      execute,
      topic: `${robot.id}/telemetry/${MESSAGE.COMMAND_ACK}`,
      isMatch: (data) => data.command === 192,
      isSuccess: (data) => data.result === 0,
    };
  };

  const gripper = (gripperId, action) => (robot) => {
    const address = getAddress(robot.id);
    const execute = () => publishCommand(robot, 'action/gripper', [[...address, gripperId, action]]);

    return {
      execute,
      topic: `${robot.id}/telemetry/${MESSAGE.COMMAND_ACK}`,
      isMatch: (data) => data.command === 211,
      isSuccess: (data) => data.result === 0,
    };
  };

  const hold = (robot) => {
    const address = getAddress(robot.id);
    publishCommand(robot, 'action/hold', [[...address]]);
  };

  const landArduPilotPlane = (parachute) => (robot) => {
    const address = getAddress(robot.id);
    const execute = () => publishCommand(robot, 'action/land/arduplane', [[...address, parachute]]);

    return {
      execute,
      topic: `${robot.id}/telemetry/${MESSAGE.COMMAND_ACK}`,
      isMatch: (data) => data.command === 208,
      isSuccess: (data) => data.result === 0,
    };
  };

  const manual = (robot) => {
    const address = getAddress(robot.id);
    publishCommand(robot, 'action/manual', [[address[0]]]);
  };

  const motorTest = (motorNumber, type, value, timeout) => (robot) => {
    const address = getAddress(robot.id);
    const execute = () => publishCommand(robot, 'action/motor_test', [[...address, motorNumber, type, value, timeout]]);

    return {
      execute,
      topic: `${robot.id}/telemetry/${MESSAGE.COMMAND_ACK}`,
      isMatch: (data) => data.command === 209,
      isSuccess: (data) => data.result === 0,
    };
  };

  const returnTo = () => (robot) => {
    const address = getAddress(robot.id);
    const execute = () => publishCommand(robot, 'action/return', [[address[0]]]);

    return {
      execute,
      topic: `${robot.id}/telemetry/${MESSAGE.COMMAND_ACK}`,
      isMatch: (data) => data.command === 176,
      isSuccess: (data) => data.result === 0,
    };
  };

  const setHome = (lat, lng, elevation) => (robot) => {
    const address = getAddress(robot.id);
    const execute = () => publishCommand(robot, 'action/set_home', [[...address, NaN, lat, lng, elevation]]);

    return {
      execute,
      topic: `${robot.id}/telemetry/${MESSAGE.COMMAND_ACK}`,
      isMatch: (data) => data.command === 179,
      isSuccess: (data) => data.result === 0,
    };
  };

  const setMode = (modeId) => (robot) => {
    const address = getAddress(robot.id);
    const execute = () => publishCommand(robot, 'action/set_mode', [[...address, modeId]]);

    return {
      execute,
      topic: `${robot.id}/telemetry/${MESSAGE.COMMAND_ACK}`,
      isMatch: (data) => data.command === 176,
      isSuccess: (data) => data.result === 0,
    };
  };

  const setServo = (number, pwm) => (robot) => {
    const address = getAddress(robot.id);
    const execute = () => publishCommand(robot, 'action/set_servo', [[...address, number, pwm]]);

    return {
      execute,
      topic: `${robot.id}/telemetry/${MESSAGE.COMMAND_ACK}`,
      isMatch: (data) => data.command === 183,
      isSuccess: (data) => data.result === 0,
    };
  };

  const takeoff = (pitch, yaw, lat, lng, alt) => (robot) => {
    const address = getAddress(robot.id);
    const execute = () => publishCommand(robot, 'action/takeoff', [[...address, pitch, yaw, lat, lng, alt]]);

    return {
      execute,
      topic: `${robot.id}/telemetry/${MESSAGE.COMMAND_ACK}`,
      isMatch: (data) => data.command === 22,
      isSuccess: (data) => data.result === 0,
    };
  };

  return {
    arm,
    brakePX4Rover,
    changeSpeed,
    disarm,
    elevateArduPilot,
    goto,
    gripper,
    hold,
    landArduPilotPlane,
    manual,
    motorTest,
    returnTo,
    setHome,
    setMode,
    setServo,
    takeoff,
  };
};

export default useActionCommand;
